import React, { ChangeEvent, useEffect, useState } from 'react';
import * as S from './ListeningFIBL.styles';
import { STAGES } from '@app/constants/stages';
import { AudioPlayer } from '@app/components/exam/AudioPlayer';
import useCountdownTimer from '@app/hooks/useCountdownTimer';
import { ScoreInfo } from '@app/components/exam/ScoreInfo';
import { useTranslation } from 'react-i18next';

interface Props {
  audioQuestion: string;
  prepTime: number;
  showAnswer: boolean;
  description: string;
  correctAnswersString: string;
  isFirstRender: React.MutableRefObject<boolean>;
  questionNumber: string;
  allowDrag: boolean;
  onAnswerChange?: (answer: string) => void;
  canStart: boolean;
}
export const ListeningFIBL: React.FC<Props> = ({
  prepTime,
  audioQuestion,
  showAnswer,
  description,
  correctAnswersString,
  questionNumber,
  allowDrag,
  onAnswerChange,
  canStart,
}) => {
  const { t } = useTranslation();
  const numberOfBlanks = (description.match(/@Blank@/g) || []).length;
  const [filledBlanks, setFilledBlanks] = useState<string[]>(new Array(numberOfBlanks).fill(''));

  const [scoreLoaded, setScoreLoaded] = useState(false);
  const [stage, setStage] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [correctAnswers, setCorrectAnswers] = useState<string[]>([]);

  const { seconds, isRunning, start, reset } = useCountdownTimer({
    initialSeconds: prepTime,
    onComplete: () => {
      setStage(STAGES.PLAYING);
      setIsPlaying(true);
    },
  });

  useEffect(() => {
    setFilledBlanks(new Array(numberOfBlanks).fill(''));
    // if (isFirstRender.current) {
    //   isFirstRender.current = false;
    //   showPermissionModal();
    // } else {
    //   initialize();
    // }
    initialize();
  }, [audioQuestion]);

  // const showPermissionModal = () => {
  //   InfoModal({
  //     title: 'Additional permissions',
  //     content: 'Click the OK button to enable record audio.',
  //     onOk: () => {
  //       initialize();
  //     },
  //   });
  // };

  const initialize = () => {
    setIsPlaying(false);
    setStage(null);
    reset();

    setFilledBlanks(new Array(numberOfBlanks).fill(''));
    if (correctAnswersString) {
      const resultArray = correctAnswersString.split(',').map((item) => item.trim());
      setCorrectAnswers(resultArray);
    }

    start();
    setStage(STAGES.START);
  };

  const handleEnded = () => {
    setIsPlaying(false);
    if (stage === STAGES.PLAYING) setStage(STAGES.COMPLETE);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
    const updatedBlanks = [...filledBlanks];
    updatedBlanks[index] = e.target.value;
    setFilledBlanks(updatedBlanks);

    if (onAnswerChange) onAnswerChange(updatedBlanks.join(','));
  };

  const textWithBlanks = description.split(/@Blank@/).map((part, index) => {
    const filledBlank = filledBlanks[index] || '';
    const isBlank = index < filledBlanks.length;
    const isCorrect = filledBlank === correctAnswers[index];

    return (
      <React.Fragment key={`part-${index}`}>
        {part}
        {isBlank && (
          <S.InputStyle
            name={`input-${index}`}
            value={filledBlank}
            onChange={(e) => handleChange(e, index)}
            style={{
              borderColor: showAnswer && scoreLoaded ? (isCorrect ? 'green' : 'red') : '#000',
            }}
          />
        )}
        {showAnswer && !isCorrect && correctAnswers[index] && (
          <S.CorrectAnswer>({correctAnswers[index]})</S.CorrectAnswer>
        )}
      </React.Fragment>
    );
  });

  return (
    <>
      <S.WrapperAudio>
        <S.Box>
          <p className="font-bold mb-2">{t('page.mockTest.audioRecorder')}</p>
          {stage === STAGES.START && (
            <S.TextRed>
              {t('page.mockTest.beginning')} {seconds}
            </S.TextRed>
          )}
          {stage === STAGES.PLAYING && <S.TextRed>{t('page.mockTest.button.playing')}</S.TextRed>}
        </S.Box>
        <AudioPlayer
          audioQuestion={audioQuestion}
          onEnded={handleEnded}
          startPlaying={isPlaying}
          waitingTime={isRunning}
          allowDrag={allowDrag}
          style={{ background: '#F1F1F1', padding: '0.7rem 0.5rem' }}
          canStart={canStart}
        />
      </S.WrapperAudio>
      <S.Wrapper>
        <S.ContenQuestion>{textWithBlanks}</S.ContenQuestion>
      </S.Wrapper>
      {showAnswer && (
        <ScoreInfo
          answer={filledBlanks.join(',') || ''}
          questionId={questionNumber}
          onLoaded={() => setScoreLoaded(true)}
        />
      )}
    </>
  );
};
